import type * as A from '@/api';
import type { ReduxSagaModel } from './types';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { handleUserError } from '@/utils/handleError';
import OrganizationAIContextService from '@/services/organizationAIContext';

const ns = 'app.models.organizationAIContext';

export type AssetKind = 'logo' | 'icon' | 'logoDark' | 'iconDark';

export interface State {
  current: A.OrganizationAIContext | null;
  loading: boolean;
  // True once the first fetch has completed for this session — lets the UI
  // distinguish "haven't loaded yet" (show spinner) from "loaded, but the org
  // has no AI-context entry yet" (render empty form). Without this, a null
  // `current` can't tell those apart.
  hasFetched: boolean;
  saving: boolean;
  uploadingKind: AssetKind | null;
  logoSignedUrl: string | null;
  iconSignedUrl: string | null;
  logoDarkSignedUrl: string | null;
  iconDarkSignedUrl: string | null;
}

const initialState: State = {
  current: null,
  loading: false,
  hasFetched: false,
  saving: false,
  uploadingKind: null,
  logoSignedUrl: null,
  iconSignedUrl: null,
  logoDarkSignedUrl: null,
  iconDarkSignedUrl: null,
};

type SaveCurrentAction = { type: 'saveCurrent'; payload: { current: State['current'] } };
type SaveLoadingAction = { type: 'saveLoading'; payload: { loading: boolean } };
type SaveHasFetchedAction = { type: 'saveHasFetched'; payload: { hasFetched: boolean } };
type SaveSavingAction = { type: 'saveSaving'; payload: { saving: boolean } };
type SaveUploadingKindAction = {
  type: 'saveUploadingKind';
  payload: { uploadingKind: State['uploadingKind'] };
};
type SaveSignedUrlAction = {
  type: 'saveSignedUrl';
  payload: { kind: AssetKind; url: string | null };
};

type Update = {
  type: 'organizationAIContext/update';
  payload: Partial<Omit<A.OrganizationAIContext, '__typename' | 'createdAt' | 'updatedAt'>>;
};

type UploadAsset = {
  type: 'organizationAIContext/uploadAsset';
  payload: { kind: AssetKind; file: File };
};

// Single place that maps each asset kind to its S3-key column on the model
// record. Keeping it next to the type so the saga and the `get` effect stay
// in lockstep when we add more variants.
const ASSET_KEY_FIELD: Record<AssetKind, 'logoS3Key' | 'iconS3Key' | 'logoDarkS3Key' | 'iconDarkS3Key'> = {
  logo: 'logoS3Key',
  icon: 'iconS3Key',
  logoDark: 'logoDarkS3Key',
  iconDark: 'iconDarkS3Key',
};

type Refresh = { type: 'organizationAIContext/get' };

const Model: ReduxSagaModel<
  State,
  {
    saveCurrent: SaveCurrentAction;
    saveLoading: SaveLoadingAction;
    saveHasFetched: SaveHasFetchedAction;
    saveSaving: SaveSavingAction;
    saveUploadingKind: SaveUploadingKindAction;
    saveSignedUrl: SaveSignedUrlAction;
  }
> = {
  namespace: 'organizationAIContext',
  state: initialState,
  effects: {
    *get(_: Refresh, { put }) {
      try {
        yield put<SaveLoadingAction>({ type: 'saveLoading', payload: { loading: true } });
        const orgId = yield* sgselect((s) => s.organization.currentOrganization);
        if (!orgId) return;
        const current = yield* sgcall(() => OrganizationAIContextService.getByOrgId(orgId));
        yield put<SaveCurrentAction>({ type: 'saveCurrent', payload: { current } });
        // Fetch a signed URL for each S3-key column the record has set.
        // Looping over the kind→field map keeps the four asset variants
        // (logo / icon / logoDark / iconDark) in one place.
        for (const kind of Object.keys(ASSET_KEY_FIELD) as AssetKind[]) {
          const s3Key = current?.[ASSET_KEY_FIELD[kind]];
          if (!s3Key) continue;
          const url = yield* sgcall(() =>
            OrganizationAIContextService.getAssetSignedUrl({ orgId, s3Key }),
          );
          yield put<SaveSignedUrlAction>({
            type: 'saveSignedUrl',
            payload: { kind, url },
          });
        }
      } catch (e) {
        handleUserError(e, `${ns}.get.error`);
      } finally {
        yield put<SaveLoadingAction>({ type: 'saveLoading', payload: { loading: false } });
        yield put<SaveHasFetchedAction>({
          type: 'saveHasFetched',
          payload: { hasFetched: true },
        });
      }
    },
    *update({ payload }: Update, { put }) {
      try {
        yield put<SaveSavingAction>({ type: 'saveSaving', payload: { saving: true } });
        const orgId = yield* sgselect((s) => s.organization.currentOrganization);
        if (!orgId) return;
        const userId = yield* sgselect((s) => s.user.currentUser?.id ?? '');
        const current = yield* sgcall(() =>
          OrganizationAIContextService.upsert({
            ...payload,
            id: orgId,
            updatedBy: userId,
          } as A.UpdateOrganizationAIContextInput),
        );
        yield put<SaveCurrentAction>({ type: 'saveCurrent', payload: { current } });
      } catch (e) {
        handleUserError(e, `${ns}.update.error`);
      } finally {
        yield put<SaveSavingAction>({ type: 'saveSaving', payload: { saving: false } });
      }
    },
    *uploadAsset({ payload: { kind, file } }: UploadAsset, { put }) {
      try {
        yield put<SaveUploadingKindAction>({
          type: 'saveUploadingKind',
          payload: { uploadingKind: kind },
        });
        const orgId = yield* sgselect((s) => s.organization.currentOrganization);
        if (!orgId) return;
        const contentSha256 = yield* sgcall(() => OrganizationAIContextService.sha256Hex(file));
        const { uploadUrl, s3Key } = yield* sgcall(() =>
          OrganizationAIContextService.getAssetUploadUrl({
            orgId,
            asset: kind,
            contentType: file.type,
            contentSha256,
            contentLength: file.size,
          }),
        );
        yield* sgcall(() => OrganizationAIContextService.putAssetToS3(uploadUrl, file));
        const fieldName = ASSET_KEY_FIELD[kind];
        yield put({
          type: 'organizationAIContext/update',
          payload: { [fieldName]: s3Key },
        } as Update);
        const url = yield* sgcall(() =>
          OrganizationAIContextService.getAssetSignedUrl({ orgId, s3Key }),
        );
        yield put<SaveSignedUrlAction>({
          type: 'saveSignedUrl',
          payload: { kind, url },
        });
      } catch (e) {
        handleUserError(e, `${ns}.uploadAsset.error`);
      } finally {
        yield put<SaveUploadingKindAction>({
          type: 'saveUploadingKind',
          payload: { uploadingKind: null },
        });
      }
    },
  },
  reducers: {
    saveCurrent(state, action) {
      return { ...state, current: action.payload.current };
    },
    saveLoading(state, action) {
      return { ...state, loading: action.payload.loading };
    },
    saveHasFetched(state, action) {
      return { ...state, hasFetched: action.payload.hasFetched };
    },
    saveSaving(state, action) {
      return { ...state, saving: action.payload.saving };
    },
    saveUploadingKind(state, action) {
      return { ...state, uploadingKind: action.payload.uploadingKind };
    },
    saveSignedUrl(state, action) {
      const { kind, url } = action.payload;
      switch (kind) {
        case 'logo':
          return { ...state, logoSignedUrl: url };
        case 'icon':
          return { ...state, iconSignedUrl: url };
        case 'logoDark':
          return { ...state, logoDarkSignedUrl: url };
        case 'iconDark':
          return { ...state, iconDarkSignedUrl: url };
        default:
          return state;
      }
    },
    resetAll() {
      return initialState;
    },
  },
};

export default Model;
